import * as React from 'react';
import { Form, Modal, ModalBody, ModalFooter, ModalSize, AlertMessage, AlertType, CustomButton, CustomSubmitButton, ButtonType, CustomDatePicker, Select } from "soprasteria-ui-library";
import { ICreateEmergencyTreatmentReport } from "../../../models/IEmergencyTreatment";
import { addToMonth, formatDate } from '../../../helpers/DateHelper';
import { useAuth } from '../../../App';
import { CarePerformerApi } from '../../../api/CarePerformerApi';
import { IListItem } from 'soprasteria-ui-library/dist/models/IFormInput';
import { setMessage } from '../../../helpers/MessageHelper';
import { NoCarePerformersFoundMessage } from '../../../utils/CarePerformerConstants';

export interface IProps {
    onSubmit: (data: ICreateEmergencyTreatmentReport) => any;
    closeForm: () => any;
    errorMessage: string;
    isLoading: boolean;
}

export const CreateEmergencyTreatmentModal: React.FunctionComponent<IProps> = ({ onSubmit, closeForm, errorMessage, isLoading }) => {
    const auth = useAuth();
    const carePerformerApi = new CarePerformerApi(auth);
    const [emergencyTreatmentDate, setEmergencyTreatmentDate] = React.useState<Date>(new Date());
    const [carePerformers, setCarePerformers] = React.useState<IListItem[]>([]);
    const [noCarePerformersMessage, setNoCarePerformersMessage] = React.useState<string>();
    const [isLoadingCarePerformers, setIsLoadingCarePerformers] = React.useState<boolean>(false);

    const dateToday = new Date();

    React.useEffect(() => {
        loadCarePerformerDentists();
    }, [emergencyTreatmentDate])

    const loadCarePerformerDentists = () => {
        setIsLoadingCarePerformers(true);
        carePerformerApi.getCarePerformerDentistsWithDate(formatDate(emergencyTreatmentDate), (carePerformersData) => {
            let mappedCarePerformers = carePerformersData.map<IListItem>((carePerformer) => { return { text: carePerformer.carePerformerName, value: carePerformer.carePerformerId.toString() } });
            setCarePerformers(mappedCarePerformers);
            if (mappedCarePerformers.length === 0) {
                const newMessage = setMessage(NoCarePerformersFoundMessage, noCarePerformersMessage ?? "");
                setNoCarePerformersMessage(newMessage);
            } else {
                setNoCarePerformersMessage("");
            }
        }, undefined, () => setIsLoadingCarePerformers(false));
    }

    const onDateOfTreatmentChange = (treatmentDate: Date | null) => {
        if (treatmentDate === null) {
            setEmergencyTreatmentDate(new Date());
        } else {
            setEmergencyTreatmentDate(treatmentDate);
        }
    }

    return (
        <Modal header="Skapa akuträkning" id="create-emergency-treatment-modal" modalSize={ModalSize.normal}>
            <Form onSubmit={onSubmit}>
                <ModalBody>
                    <AlertMessage alertType={AlertType.danger} duration={5000}>{errorMessage}</AlertMessage>
                    <AlertMessage alertType={AlertType.danger} duration={10000}>{noCarePerformersMessage}</AlertMessage>
                    <CustomDatePicker
                        name="dateOfTreatment"
                        label="Välj datum för utförd akuttandvård"
                        value={emergencyTreatmentDate}
                        min={addToMonth(emergencyTreatmentDate, -3)}
                        max={dateToday}
                        onChange={onDateOfTreatmentChange}
                        required
                    />
                    <Select name="carePerformerId"
                        label="Behandlare"
                        placeholder="Välj en behandlare"
                        options={carePerformers}
                        isLoading={isLoadingCarePerformers}
                        required />
                </ModalBody>
                <ModalFooter>
                    <div className="text-right btn-group" role="group">
                        <CustomButton buttonType={ButtonType.general} data-dismiss="modal" id="close-modal" onClick={() => closeForm()} buttonText="Avbryt" />
                        <CustomSubmitButton isLoading={isLoading}>Skapa</CustomSubmitButton>
                    </div>
                    <input type="hidden" data-toggle="modal" id="open-modal" data-target="#create-emergency-treatment-modal" />
                </ModalFooter>
            </Form>
        </Modal >
    );
}